import { useSeoTags } from "@/lib/seo";
import { openConsentBanner } from "@/lib/analytics";

/**
 * VOIHU Cookie Policy — approved verbatim text.
 * Do not edit the wording. Layout / spacing / typography may be tuned;
 * substantive text may not. The Privacy Policy reference is rendered as
 * a link to /privacy per implementation spec (not a wording change).
 */
export default function CookiesPage() {
  useSeoTags({
    title: "Cookie Policy — VOIHU",
    description:
      "How VOIHU uses cookies, browser storage and similar technologies.",
    canonical: "https://voihuworld.com/cookies",
  });

  return (
    <main className="bg-[color:var(--oh-bg)] text-[color:var(--oh-ink)]"
          data-testid="cookies-page">
      <article className="max-w-[720px] mx-auto px-6 sm:px-10 pt-16 pb-24">
        <div className="oh-label" data-testid="cookies-eyebrow">Legal</div>

        <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4"
            data-testid="cookies-h1">
          VOIHU Cookie Policy
        </h1>

        <p className="oh-label mt-6" data-testid="cookies-updated">Last updated: September 2026</p>

        <div className="mt-10 space-y-6 text-base sm:text-[17px] leading-[1.75]"
             data-testid="cookies-body">
          <p>
            This Cookie Policy explains how VOIHU uses cookies, browser storage
            and similar technologies when you use the VOIHU website and
            service.
          </p>

          <p>VOIHU is operated by:</p>
          <Address>
            <div>CA HOLDING S.R.L.S.</div>
            <div>Viale Italia 8/C</div>
            <div>25126 Brescia (BS)</div>
            <div>Italy</div>
            <div>Italian Tax Code / VAT No.: 04672180983</div>
            <div>REA: BS-632332</div>
            <div>Certified Email (PEC): caholding@pec.it</div>
            <div>Service / trading name: VOIHU</div>
            <div>Website: voihuworld.com</div>
            <div>
              Support:{" "}
              <a href="mailto:support@voihuworld.com"
                 className="underline underline-offset-4">support@voihuworld.com</a>
            </div>
          </Address>

          <p>
            This Cookie Policy should be read together with the VOIHU{" "}
            <a href="/privacy" className="underline underline-offset-4"
               data-testid="cookies-privacy-link">Privacy Policy</a>.
          </p>

          <Section n="1" title="What cookies and similar technologies are">
            <p>
              Cookies are small pieces of information that may be stored by a
              website in a user’s browser.
            </p>
            <p>
              Web applications may also use similar browser-storage
              technologies, such as localStorage and sessionStorage, to
              maintain sessions, remember temporary information or support
              technical functionality.
            </p>
            <p>
              In this Policy, references to “cookies and similar technologies”
              include these types of browser storage where appropriate.
            </p>
          </Section>

          <Section n="2" title="VOIHU’s approach">
            <p>VOIHU is designed to minimise unnecessary tracking.</p>
            <p>VOIHU does not currently use cookies or similar technologies for:</p>
            <List>
              <li>behavioural advertising;</li>
              <li>cross-site advertising profiles;</li>
              <li>advertising retargeting;</li>
              <li>third-party audience profiling;</li>
              <li>the sale of behavioural data;</li>
              <li>tracking users across unrelated websites.</li>
            </List>
            <p>
              VOIHU currently uses only limited technical and functional
              browser-storage mechanisms necessary to operate the service or
              support user-requested functionality.
            </p>
          </Section>

          <Section n="3" title="Technologies currently used by VOIHU">
            <Sub title="Authentication session">
              <p>
                VOIHU uses authentication technology to keep a user securely
                signed in after authentication.
              </p>
              <p>
                The service currently uses an authentication session cookie
                with security protections including HttpOnly, Secure and
                SameSite controls.
              </p>
              <p>
                The authentication session is necessary to provide
                account-based functionality.
              </p>
              <p>Authentication sessions expire after a limited period.</p>
            </Sub>

            <Sub title="Local browser storage">
              <p>
                VOIHU may use localStorage to retain an opaque
                authentication-related value used by the application as part
                of authenticated access.
              </p>
              <p>
                This value is not used for advertising or cross-site
                profiling.
              </p>
            </Sub>

            <Sub title="Temporary session storage">
              <p>
                VOIHU uses sessionStorage for limited first-party technical
                purposes.
              </p>
              <p>This may include:</p>
              <List>
                <li>
                  a random per-tab session identifier used to reduce
                  accidental duplicate event counting;
                </li>
                <li>
                  temporary source or campaign information used to understand
                  how a user arrived at a MESSAGE.
                </li>
              </List>
              <p>
                This information is limited to the current browser session or
                tab as applicable.
              </p>
              <p>
                It is not used to build advertising profiles or follow users
                across unrelated websites.
              </p>
            </Sub>
          </Section>

          <Section n="4" title="First-party analytics">
            <p>
              VOIHU uses limited first-party event information to understand
              how MESSAGEs are viewed, supported and passed on.
            </p>
            <p>These events may relate to functions such as:</p>
            <List>
              <li>opening or viewing a MESSAGE;</li>
              <li>adding a VOICE;</li>
              <li>opening or using sharing functionality;</li>
              <li>referral relationships;</li>
              <li>propagation of a MESSAGE.</li>
            </List>
            <p>
              VOIHU’s first-party application analytics do not use a
              third-party advertising or behavioural analytics platform.
            </p>
            <p>
              VOIHU does not persist device fingerprints, advertising
              identifiers, User-Agent strings or IP addresses as part of these
              application analytics records.
            </p>
            <p>
              These analytics are intended to operate and understand VOIHU’s
              own service rather than create commercial behavioural profiles.
            </p>

            <Sub title="Optional third-party analytics — Google Analytics 4">
              <p>
                In addition to the first-party analytics described above,
                VOIHU offers optional website measurement through Google
                Analytics 4.
              </p>
              <p>
                Google Analytics 4 helps VOIHU understand overall website
                traffic, traffic sources such as social platforms and
                campaigns, landing pages and device/region information
                provided by Google Analytics.
              </p>
              <p>
                Google Analytics 4 is not used for advertising, remarketing
                or behavioural profiling.
              </p>
              <p>
                Google Analytics 4 does not load and does not receive any
                analytics data until the visitor explicitly accepts analytics
                cookies through the VOIHU analytics-consent banner. Advertising
                storage, ad-user-data and ad-personalization signals remain
                denied at all times.
              </p>
              <p>
                Visitors can change their analytics preference at any time
                using the “Manage analytics preference” control below.
              </p>
              <p className="mt-3">
                <button
                  type="button"
                  onClick={() => openConsentBanner()}
                  className="underline text-[color:var(--oh-ink)]"
                  data-testid="cookies-manage-analytics-btn"
                >
                  Manage analytics preference
                </button>
              </p>
            </Sub>
          </Section>

          <Section n="5" title="Stripe and payment technologies">
            <p>
              When a user chooses to make a payment, VOIHU uses Stripe to
              provide payment functionality.
            </p>
            <p>
              Stripe may use cookies or similar technologies that are
              necessary to:
            </p>
            <List>
              <li>process payments;</li>
              <li>prevent fraud;</li>
              <li>secure transactions;</li>
              <li>authenticate payment methods;</li>
              <li>
                provide payment features such as supported wallet or card
                functionality.
              </li>
            </List>
            <p>
              These technologies are associated with a user-initiated payment
              process.
            </p>
            <p>Stripe operates under its own privacy and technology policies.</p>
            <p>VOIHU does not use Stripe payment technology for advertising profiling.</p>
          </Section>

          <Section n="6" title="Google authentication">
            <p>
              Where a user actively chooses to sign in using Google, Google
              authentication services may process technical information or use
              technologies necessary to authenticate the user.
            </p>
            <p>
              These technologies are activated in connection with the user’s
              choice to use Google authentication.
            </p>
            <p>VOIHU does not use Google authentication for advertising tracking.</p>
          </Section>

          <Section n="7" title="Social sharing">
            <p>
              VOIHU provides links and controls that allow users to share
              MESSAGEs through external services.
            </p>
            <p>
              Ordinary browsing of VOIHU does not require VOIHU to load
              passive advertising trackers from social networks.
            </p>
            <p>
              When a user actively chooses to open or share through a
              third-party service, that third party may use its own cookies or
              similar technologies according to its own policies.
            </p>
            <p>
              VOIHU does not control cookies set directly by third-party
              websites once the user leaves VOIHU or activates the relevant
              external service.
            </p>
          </Section>

          <Section n="8" title="Geocoding">
            <p>
              VOIHU currently uses a backend-mediated geocoding process to
              help users select a city.
            </p>
            <p>
              The browser sends the place search to VOIHU, and VOIHU’s backend
              communicates with the relevant geocoding infrastructure.
            </p>
            <p>
              VOIHU does not rely on third-party browser cookies for ordinary
              city search and does not request device GPS location through
              this feature.
            </p>
          </Section>

          <Section n="9" title="Essential and functional technologies">
            <p>
              The technologies currently used during ordinary VOIHU browsing
              are intended to be technical or functional technologies
              necessary for the service requested by the user.
            </p>
            <p>They support functions such as:</p>
            <List>
              <li>authentication;</li>
              <li>security;</li>
              <li>authenticated access;</li>
              <li>temporary session continuity;</li>
              <li>first-party event deduplication;</li>
              <li>source attribution relating to MESSAGE propagation;</li>
              <li>payment functionality when initiated by the user.</li>
            </List>
            <p>
              VOIHU does not currently use non-essential advertising or
              profiling cookies during ordinary browsing.
            </p>
          </Section>

          <Section n="10" title="Consent for optional analytics cookies">
            <p>
              VOIHU shows an analytics-consent banner on first visit so
              visitors can accept or reject optional analytics cookies used
              by Google Analytics 4.
            </p>
            <p>
              Google Analytics 4 is loaded and receives data only after a
              visitor explicitly accepts analytics cookies. If the visitor
              rejects, or has not yet decided, Google Analytics remains
              blocked and no analytics data is sent.
            </p>
            <p>
              VOIHU does not currently deploy non-essential advertising or
              behavioural-profiling technologies during ordinary browsing.
              Accordingly, VOIHU does not present a separate advertising
              consent surface for technologies it does not use.
            </p>
            <p>
              If VOIHU introduces additional technologies that require prior
              consent under applicable law, VOIHU will reassess its consent
              mechanism before activating those technologies.
            </p>
          </Section>

          <Section n="11" title="Managing browser storage">
            <p>
              Users can control cookies and website storage through their
              browser settings.
            </p>
            <p>Depending on the browser, users may be able to:</p>
            <List>
              <li>view stored cookies and site data;</li>
              <li>delete cookies and site data;</li>
              <li>block cookies;</li>
              <li>clear localStorage or other website data;</li>
              <li>configure privacy settings.</li>
            </List>
            <p>
              Blocking or deleting technologies that are necessary for
              authentication or security may prevent some VOIHU functions from
              working correctly or may sign the user out.
            </p>
          </Section>

          <Section n="12" title="No advertising profiles">
            <p>
              VOIHU does not currently use the browser technologies described
              in this Policy to create advertising profiles of users.
            </p>
            <p>VOIHU does not currently use:</p>
            <List>
              <li>Meta Pixel;</li>
              <li>Google Ads conversion tracking;</li>
              <li>advertising retargeting pixels;</li>
              <li>third-party behavioural advertising networks;</li>
              <li>cross-site advertising identifiers.</li>
            </List>
            <p>
              If this changes, this Cookie Policy and, where required,
              VOIHU’s consent mechanisms will be updated before such
              technologies are used in a manner requiring user consent.
            </p>
          </Section>

          <Section n="13" title="Changes to this Cookie Policy">
            <p>VOIHU may update this Cookie Policy if:</p>
            <List>
              <li>browser-storage practices change;</li>
              <li>new technical functionality is introduced;</li>
              <li>new service providers are adopted;</li>
              <li>legal or regulatory requirements change;</li>
              <li>non-essential tracking technologies are considered.</li>
            </List>
            <p>
              The “Last updated” date at the beginning of this Policy
              indicates the latest revision.
            </p>
            <p>
              Material changes may be communicated through VOIHU where
              appropriate.
            </p>
          </Section>

          <Section n="14" title="Contact">
            <p>
              Questions about cookies, browser storage or privacy can be sent
              to:
            </p>
            <Address>
              <div>CA HOLDING S.R.L.S.</div>
              <div>Viale Italia 8/C</div>
              <div>25126 Brescia (BS)</div>
              <div>Italy</div>
              <div>C.F./VAT No.: 04672180983</div>
              <div>REA: BS-632332</div>
              <div>PEC: caholding@pec.it</div>
            </Address>
            <p>Support and privacy requests:</p>
            <p>
              <a href="mailto:support@voihuworld.com"
                 className="underline underline-offset-4">support@voihuworld.com</a>
            </p>
          </Section>
        </div>
      </article>
    </main>
  );
}


/* ── layout helpers (shared style with /privacy and /terms) ─────────────── */

function Section({ n, title, children }) {
  return (
    <section className="mt-14 pt-10 border-t oh-hairline"
             data-testid={`cookies-section-${n}`}>
      <div className="oh-label">Section {n}</div>
      <h2 className="font-serif-editorial text-2xl sm:text-3xl leading-[1.15] mt-3">
        {title}
      </h2>
      <div className="mt-6 space-y-5">{children}</div>
    </section>
  );
}

function Sub({ title, children }) {
  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold mt-2 mb-3">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function List({ children }) {
  return (
    <ul className="list-disc pl-6 space-y-2 marker:text-[color:var(--oh-ink-3)]">
      {children}
    </ul>
  );
}

function Address({ children }) {
  return (
    <address className="not-italic leading-[1.75] my-2">{children}</address>
  );
}
